import { zodResolver } from '@hookform/resolvers/zod'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogTrigger } from '../../../../components/ui/alert-dialog'
import { Button } from '../../../../components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../components/ui/form'
import { Input } from '../../../../components/ui/input'
import { Label } from '../../../../components/ui/label'
import { Switch } from '../../../../components/ui/switch'
import { Textarea } from '../../../../components/ui/textarea'
import { RoomsFormData, roomsSchema } from '../../../../validators/rooms-validator'
import { createRoom } from '../actions/create-room'

interface FormRoomsProps {
  updateRomms: () => void
}

export function FormRooms({ updateRomms }: FormRoomsProps) {
  const [modal, setModal] = useState(false)

  const openModal = () => setModal(true)
  const closeModal = () => setModal(false)

  const form = useForm<RoomsFormData>({
    resolver: zodResolver(roomsSchema),
    defaultValues: {
      disponibilidade: true
    }
  })

  const onSubmit = async (data: RoomsFormData) => {
    console.debug(data)
    await createRoom(data)
    form.reset()

    updateRomms()
    closeModal()
  }

  return (
    <AlertDialog open={modal}>
      <AlertDialogTrigger asChild onClick={openModal}>
        <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2">
          <Plus />
          Sala
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="min-w-[600px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col m-auto gap-4">
            <h3 className="text-3xl text-acelerait-black">Adicionar sala</h3>

            <div className="grid grid-cols-2 gap-3 w-full">
              <FormField
                control={form.control}
                name="nome"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <span className="text-gray-400">*</span> Nome da sala
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nome da sala" />
                    </FormControl>
                    <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="numero"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <span className="text-gray-400"></span> Número da sala
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Número da sala" />
                    </FormControl>
                    <FormMessage className="text-xs">{form.formState.errors.numero?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-[1fr_140px] gap-4">
              <FormField
                control={form.control}
                name="recursos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <span className="text-gray-400"></span> Recursos
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Recursos" />
                    </FormControl>
                    <FormMessage className="text-xs">{form.formState.errors.recursos?.message}</FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="capacidade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <span className="text-gray-400"></span> Capacidade
                    </FormLabel>
                    <FormControl>
                      <Input {...field} onChange={(e) => field.onChange(parseInt(e.target.value) || 1)} placeholder="Capacidade" min={0} />
                    </FormControl>
                    <FormMessage className="text-xs">{form.formState.errors.capacidade?.message}</FormMessage>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="observacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400"></span> Observações
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Observações" className="resize-none" />
                  </FormControl>
                  <FormMessage className="text-xs">{form.formState.errors.observacao?.message}</FormMessage>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="disponibilidade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <span className="text-gray-400">*</span> Disponibilidade
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center">
                      <Label className="flex gap-2 items-center  mt-2">
                        <Switch onCheckedChange={field.onChange} checked={field.value} />
                        Sala disponivel?
                      </Label>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
            <AlertDialogFooter className="grid grid-cols-[1fr_80px] items-center justify-center">
              <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-muted ">
                Enviar
              </Button>
              <AlertDialogCancel onClick={closeModal}>Fechar</AlertDialogCancel>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
